/**
 * authForcedLogoutMarker — reason-coded instrumentation for the "forced to sign
 * in again" investigation (E2, investigate-first).
 *
 * Cedar has several paths that route a user back to login / clear their tokens.
 * Pre-instrumentation they were indistinguishable in the field, so we couldn't
 * tell the dominant cause (H1 SecureStore/keychain orphaning on app update vs
 * H2 the 7-day local JWT wall vs H3 Auth0 rotation). This emits ONE greppable
 * marker per path so a reported user's device logs name the cause.
 *
 * Why a console marker and NOT a backend beacon: every forced-logout moment is
 * exactly when the token is missing/invalid, so an auth-required /api/events
 * POST would itself 401 and drop the event (no_token_at_boot has no token to
 * send at all). The reliable signal is device-side. For diagnosis: pull the
 * user's device logs, grep `AUTH_FORCED_LOGOUT`, and correlate with the Auth0
 * dashboard's re-auth events for the population rate.
 */

export type AuthLogoutReason =
  | 'no_token_at_boot'           // returning user, no stored tokens (a keychain that refused is not this — see below)
  | 'no_cedar_jwt'               // tokens existed but the cedar_jwt was absent
  | 'keychain_unreadable'        // on screen and the keychain still refused; credentials kept (auth/bootCredentials.ts)
  | 'jwt_expired_local'          // AuthContext local expiry guard fired — H2 7-day wall
  | 'verify_unauthorized'        // cold-start /api/auth/me returned 401/403
  | 'bg_reconcile_unauthorized'  // fast-path background verify returned 401/403
  | 'no_user_id'                 // /api/auth/me 200 but no user_id in the body
  | 'onboarding_unauthorized'    // onboarding-state resolver returned 401
  | 'held_session_refused'       // a session kept after an inconclusive boot verify, refused on its re-check (auth/heldSessionWatch.ts)
  | 'unknown_boot_error';        // catch-all (corrupt storage / JS crash)

/** Pure — the exact marker string (so the format is unit-pinned). */
export function forcedLogoutMarker(reason: AuthLogoutReason): string {
  return `AUTH_FORCED_LOGOUT reason=${reason}`;
}

/** Emit the device-level marker at a forced-logout site. */
export function markForcedLogout(reason: AuthLogoutReason): void {
  console.warn(forcedLogoutMarker(reason));
}
